import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneHierarchy } from 'three/examples/jsm/utils/SkeletonUtils.js';

/**
 * The player's car: a Ford Ka converted out of GTA San Andreas assets, with a box-built
 * hatchback standing in until the model lands — and permanently if it never does.
 *
 * `createCarMesh()` is called synchronously while the world is being built, so it always
 * returns a working `CarMesh` immediately. The GLB is fetched once for the whole page and
 * every car clones that one prototype; when the fetch resolves, each car quietly swaps its
 * primitive out for a clone. If it 404s or fails to parse, nothing swaps and the game runs
 * on the primitives, which is exactly what it did before the model existed.
 */

export interface CarMesh {
  group: THREE.Group;
  /** Set the body colour after construction (e.g. when a player changes it in the lobby). */
  setColor(color: number): void;
  /** Spin the wheels and lean the body; `slip` drives the roll into a slide. */
  animate(speed: number, steer: number, slip: number, dt: number): void;
  setBraking(braking: boolean): void;
  /** How far the headlights are on, 0..1. Follows the same dusk ramp as the city's. */
  setLights(intensity: number): void;
  dispose(): void;
}

const MODEL_URL = '/models/fordka.glb';

/**
 * What the physics thinks it is driving: a 1.15 m collision circle, roughly half a car's
 * width. The model is scaled to fit inside both figures rather than to either one, so it
 * can never be wider than the collision radius implies.
 */
const TARGET_LENGTH = 4.1;
const TARGET_WIDTH = 1.86;

/** Radius of the fake contact shadow, and how far above the tarmac it floats. */
const SHADOW_RADIUS = 1.85;
const SHADOW_HEIGHT = 0.22;

// ---------------------------------------------------------------------------
// the shared prototype
// ---------------------------------------------------------------------------

interface Prototype {
  /** Cloned per car. Already oriented, scaled and sitting on y = 0. */
  root: THREE.Object3D;
  /** The material a player's colour is painted onto, if one could be identified. */
  paint: THREE.Material | null;
  /** Materials lit up under braking. */
  brake: THREE.Material[];
  wheelRadius: number;
}

/** Resolves to `null` rather than rejecting, so a missing model is never an unhandled error. */
let prototypePromise: Promise<Prototype | null> | null = null;

function fordKaPrototype(): Promise<Prototype | null> {
  if (!prototypePromise) {
    prototypePromise = new GLTFLoader()
      .loadAsync(MODEL_URL)
      .then((gltf) => buildPrototype(gltf.scene))
      .catch((error: unknown) => {
        console.warn(`[carMesh] ${MODEL_URL} did not load; using the primitive car.`, error);
        return null;
      });
  }
  return prototypePromise;
}

const WHEEL_NODE = /^wheel_[lr][fb]/i;
const FRONT_WHEEL_NODE = /^wheel_[lr]f/i;
const LEFT_WHEEL_NODE = /^wheel_l/i;
/** Marks the group inside each wheel dummy that carries the rolling rotation. */
const SPIN_SUFFIX = '-spin';

/**
 * GTA vehicles carry a "car colour remap" material: a flat, untextured material whose
 * colour the engine overwrites per spawn. On this model it is called `primary` and is the
 * only untextured material on the body panels.
 */
const PAINT_MATERIAL = /(^|[^a-z])(primary|remap|carcolou?r|bodycolou?r|paint)([^a-z]|$)/i;
/** Rear lamp lenses. Combined with a rear-of-car position test — see `buildPrototype`. */
const LIGHT_MATERIAL = /(faro|tail|rear.?light|brake|stop|lights)/i;

/**
 * Turns the loaded glTF scene into something the game can clone.
 *
 * What the file actually contains, measured off the glTF rather than assumed: the Blender
 * exporter has already done the GTA Z-up → glTF Y-up conversion, so the model arrives
 * **+Y up with its nose at -Z** — the game's convention exactly, no rotation needed. It is
 * also already close to real metres: 3.985 m long, 1.878 m wide, tyres bottoming out at
 * y = -0.666 with the origin at sill height. So the only corrections are a ~0.99 uniform
 * scale, a lift onto the ground plane, and centring the body on the origin. Everything is
 * measured here rather than hard-coded, so swapping the GLB for another car still works.
 */
function buildPrototype(scene: THREE.Object3D): Prototype {
  const car = (scene.children[0] ?? scene) as THREE.Object3D;
  scene.updateMatrixWorld(true);

  // --- orientation and scale ------------------------------------------------
  // Measured while the model is still at identity, so everything below is in model units.
  const box = new THREE.Box3().setFromObject(car);
  const size = box.getSize(new THREE.Vector3());
  // Fit inside both targets rather than to one of them: a car wider than 1.86 m would
  // visibly overhang the collision circle the physics is actually using.
  const scale = Math.min(TARGET_LENGTH / size.z, TARGET_WIDTH / size.x);
  const centre = box.getCenter(new THREE.Vector3());

  // --- wheels ---------------------------------------------------------------
  // Named `wheel_lf_dummy` … `wheel_rb_dummy`, each holding one wheel mesh. All four
  // reference the same geometry un-mirrored, the way GTA ships them — the engine mirrors
  // the left pair at spawn. Without that the left rims face into the car.
  const wheelRadius = prepareWheels(car) * scale;

  const root = new THREE.Group();
  root.name = 'fordka';
  const model = new THREE.Group();
  model.name = 'fordka-model';
  model.scale.setScalar(scale);
  // Centre the body on the car's position and stand the tyres on the road.
  model.position.set(-centre.x * scale, -box.min.y * scale, -centre.z * scale);
  model.add(car);
  root.add(model);

  // --- body roll ------------------------------------------------------------
  // Everything except the wheels goes under one group so the shell can lean without the
  // tyres leaning with it and cutting into the road.
  const tilt = new THREE.Group();
  tilt.name = 'car-tilt';
  for (const child of [...car.children]) {
    if (WHEEL_NODE.test(child.name)) continue;
    tilt.add(child);
  }
  car.add(tilt);

  // --- materials ------------------------------------------------------------
  const wheelMaterials = new Set<THREE.Material>();
  const bodyTriangles = new Map<THREE.Material, number>();
  const materialBox = new Map<THREE.Material, THREE.Box3>();
  root.updateMatrixWorld(true);
  root.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    const onWheel = isUnderWheel(mesh);
    for (const material of materialsOf(mesh)) {
      if (onWheel) {
        wheelMaterials.add(material);
        continue;
      }
      bodyTriangles.set(material, (bodyTriangles.get(material) ?? 0) + triangleCount(mesh));
      const bounds = materialBox.get(material) ?? new THREE.Box3().makeEmpty();
      // This mesh's own geometry, not `setFromObject`, which folds in every descendant —
      // the tail lamp node has the front bumper parented under it and would look like it
      // spanned the whole car.
      mesh.geometry.computeBoundingBox();
      const own = mesh.geometry.boundingBox;
      if (own) bounds.union(own.clone().applyMatrix4(mesh.matrixWorld));
      materialBox.set(material, bounds);
    }
  });

  let paint: THREE.Material | null = null;
  for (const material of bodyTriangles.keys()) {
    if (PAINT_MATERIAL.test(material.name)) paint = material;
  }
  if (!paint) {
    // Nothing named itself. Take the biggest untextured material on the body — the paint
    // is the one panel colour that cannot come from a texture, because the game recolours
    // it. Wheels are excluded or a chrome rim wins on triangle count alone.
    let best = 0;
    for (const [material, triangles] of bodyTriangles) {
      if (wheelMaterials.has(material) || isTextured(material) || triangles <= best) continue;
      best = triangles;
      paint = material;
    }
  }
  if (paint) {
    const standard = paint as THREE.MeshStandardMaterial;
    // Untextured GTA paint exports as fully rough, which reads as primer. Tighten it up
    // so the sun puts a highlight down the flank.
    standard.roughness = 0.45;
    standard.metalness = 0;
  }

  // Rear lamps only: `vehiclelights128.001` is the window and indicator trim and runs the
  // whole length of the car, so a name match on its own would light up the headlights too.
  const brake: THREE.Material[] = [];
  for (const [material, bounds] of materialBox) {
    if (!LIGHT_MATERIAL.test(material.name)) continue;
    if (bounds.min.z <= 0) continue;
    if (!(material as THREE.MeshStandardMaterial).emissive) continue;
    brake.push(material);
  }

  return { root, paint, brake, wheelRadius };
}

/**
 * Gives every wheel dummy a `-spin` child to rotate, mirrors the left-hand pair, and
 * reports the tyre radius in model units. The extra group matters: the dummy itself has to
 * stay free to steer about Y, and stacking a spin on the same node would tilt that axis.
 */
function prepareWheels(car: THREE.Object3D): number {
  car.updateMatrixWorld(true);
  let radius = 0;
  for (const child of car.children) {
    if (!WHEEL_NODE.test(child.name)) continue;
    if (radius === 0) {
      radius = new THREE.Box3().setFromObject(child).getSize(new THREE.Vector3()).y / 2;
    }
    const spinner = new THREE.Group();
    spinner.name = `${child.name}${SPIN_SUFFIX}`;
    for (const part of [...child.children]) spinner.add(part);
    child.add(spinner);
    // Mirroring in X commutes with the spin (also about X) and sits inside the steering
    // rotation, so both still behave; three flips the winding for us on a negative scale.
    if (LEFT_WHEEL_NODE.test(child.name)) child.scale.x = -1;
  }
  return radius || 0.33;
}

function isUnderWheel(object: THREE.Object3D): boolean {
  for (let node: THREE.Object3D | null = object; node; node = node.parent) {
    if (WHEEL_NODE.test(node.name)) return true;
  }
  return false;
}

function materialsOf(mesh: THREE.Mesh): THREE.Material[] {
  return Array.isArray(mesh.material) ? mesh.material : [mesh.material];
}

function isTextured(material: THREE.Material): boolean {
  return (material as THREE.MeshStandardMaterial).map != null;
}

function triangleCount(mesh: THREE.Mesh): number {
  const geometry = mesh.geometry;
  return (geometry.index?.count ?? geometry.getAttribute('position')?.count ?? 0) / 3;
}

// ---------------------------------------------------------------------------
// one car
// ---------------------------------------------------------------------------

/** The bit of a car that can be swapped out: primitive first, Ford Ka once it loads. */
interface CarRig {
  root: THREE.Object3D;
  setColor(color: number): void;
  animate(speed: number, steer: number, slip: number, dt: number): void;
  setBraking(braking: boolean): void;
  dispose(): void;
}

const BRAKE_ON = new THREE.Color(0xff2a10);
const BRAKE_OFF = new THREE.Color(0x000000);

function instantiate(prototype: Prototype): CarRig {
  const root = cloneHierarchy(prototype.root);

  // `clone` shares geometries *and* materials with the prototype. Geometry sharing is what
  // makes eight cars cheap and is left alone; materials have to be per-car or every player
  // ends up the same colour.
  const clonedMaterials = new Map<THREE.Material, THREE.Material>();
  const cloneMaterial = (source: THREE.Material): THREE.Material => {
    let copy = clonedMaterials.get(source);
    if (!copy) {
      copy = source.clone();
      clonedMaterials.set(source, copy);
    }
    return copy;
  };
  root.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.material = Array.isArray(mesh.material)
      ? mesh.material.map(cloneMaterial)
      : cloneMaterial(mesh.material);
  });

  const paint = prototype.paint
    ? (clonedMaterials.get(prototype.paint) as THREE.MeshStandardMaterial | undefined)
    : undefined;
  const brake = prototype.brake
    .map((material) => clonedMaterials.get(material) as THREE.MeshStandardMaterial | undefined)
    .filter((material): material is THREE.MeshStandardMaterial => material !== undefined);

  const tilt = root.getObjectByName('car-tilt') ?? null;
  // The `-spin` groups roll; their parent dummies steer. Collected separately so neither
  // rotation lands on the same node as the other.
  const spinners: THREE.Object3D[] = [];
  const steered: THREE.Object3D[] = [];
  root.traverse((object) => {
    if (!object.name.endsWith(SPIN_SUFFIX)) return;
    spinners.push(object);
    const hub = object.parent;
    if (hub && FRONT_WHEEL_NODE.test(hub.name)) steered.push(hub);
  });

  const radius = prototype.wheelRadius;
  let spin = 0;
  let roll = 0;

  return {
    root,
    setColor(color: number) {
      paint?.color.setHex(color, THREE.SRGBColorSpace);
    },
    animate(speed: number, steer: number, slip: number, dt: number) {
      spin -= (speed / radius) * dt;
      for (const wheel of spinners) wheel.rotation.x = spin;
      for (const wheel of steered) wheel.rotation.y = steer * 0.45;
      if (tilt) {
        // Shallower than the primitive's lean: a real body shell that far off the wheels
        // starts showing daylight under the inside tyres.
        const target = THREE.MathUtils.clamp(-steer * 0.045 - slip * 0.009, -0.11, 0.11);
        roll += (target - roll) * Math.min(1, dt * 8);
        tilt.rotation.z = roll;
      }
    },
    setBraking(braking: boolean) {
      for (const material of brake) {
        material.emissive.copy(braking ? BRAKE_ON : BRAKE_OFF);
        material.emissiveIntensity = braking ? 1.6 : 1;
      }
    },
    dispose() {
      // Only the per-car material copies. The geometries and textures belong to the cached
      // prototype and are still in use by every other car on the grid.
      for (const material of clonedMaterials.values()) material.dispose();
      root.removeFromParent();
    },
  };
}

/**
 * The original stylised hatchback, built from boxes.
 *
 * Still here because it needs no network, no parse and no frame budget: it is what every
 * car looks like for the moment before the GLB arrives, and what they all look like
 * forever if it doesn't.
 */
function buildPrimitiveRig(): CarRig {
  const root = new THREE.Group();
  root.name = 'car-primitive';

  const disposables: { dispose(): void }[] = [];
  const bodyMat = new THREE.MeshLambertMaterial({ color: 0xffffff });
  const darkMat = new THREE.MeshLambertMaterial({ color: 0x1b1d22 });
  const glassMat = new THREE.MeshLambertMaterial({ color: 0x28323c });
  const lightMat = new THREE.MeshBasicMaterial({ color: 0xfff0c4 });
  const brakeMat = new THREE.MeshBasicMaterial({ color: 0x581410 });
  disposables.push(bodyMat, darkMat, glassMat, lightMat, brakeMat);

  // Body. Forward is -Z, so the nose is at negative Z.
  const lower = new THREE.BoxGeometry(1.86, 0.62, 4.1);
  const lowerMesh = new THREE.Mesh(lower, bodyMat);
  lowerMesh.position.y = 0.62;
  root.add(lowerMesh);
  disposables.push(lower);

  const cabin = new THREE.BoxGeometry(1.66, 0.62, 2.1);
  const cabinMesh = new THREE.Mesh(cabin, bodyMat);
  cabinMesh.position.set(0, 1.2, 0.16);
  root.add(cabinMesh);
  disposables.push(cabin);

  const glass = new THREE.BoxGeometry(1.68, 0.4, 1.5);
  const glassMesh = new THREE.Mesh(glass, glassMat);
  glassMesh.position.set(0, 1.26, 0.1);
  root.add(glassMesh);
  disposables.push(glass);

  // A roof stripe so players can tell cars apart from directly behind.
  const stripe = new THREE.BoxGeometry(0.34, 0.06, 3.9);
  const stripeMat = new THREE.MeshLambertMaterial({ color: 0xf2f2f2 });
  const stripeMesh = new THREE.Mesh(stripe, stripeMat);
  stripeMesh.position.set(0, 1.53, 0.16);
  root.add(stripeMesh);
  disposables.push(stripe, stripeMat);

  const headlights = new THREE.BoxGeometry(1.4, 0.16, 0.1);
  const headlightMesh = new THREE.Mesh(headlights, lightMat);
  headlightMesh.position.set(0, 0.74, -2.03);
  root.add(headlightMesh);
  disposables.push(headlights);

  const tailGeo = new THREE.BoxGeometry(1.4, 0.16, 0.1);
  const tailMesh = new THREE.Mesh(tailGeo, brakeMat);
  tailMesh.position.set(0, 0.76, 2.03);
  root.add(tailMesh);
  disposables.push(tailGeo);

  // Wheels. Cylinders lie along X once rotated, which is also the axis they spin about.
  const radius = 0.36;
  const wheelGeo = new THREE.CylinderGeometry(radius, radius, 0.3, 12);
  wheelGeo.rotateZ(Math.PI / 2);
  disposables.push(wheelGeo);
  const wheels: THREE.Mesh[] = [];
  const frontWheels: THREE.Mesh[] = [];
  for (const [x, z, front] of [
    [-0.92, -1.35, true],
    [0.92, -1.35, true],
    [-0.92, 1.4, false],
    [0.92, 1.4, false],
  ] as [number, number, boolean][]) {
    const wheel = new THREE.Mesh(wheelGeo, darkMat);
    wheel.position.set(x, radius, z);
    root.add(wheel);
    wheels.push(wheel);
    if (front) frontWheels.push(wheel);
  }

  let spin = 0;
  let roll = 0;

  return {
    root,
    setColor(color: number) {
      bodyMat.color.setHex(color, THREE.SRGBColorSpace);
    },
    animate(speed: number, steer: number, slip: number, dt: number) {
      spin -= (speed / radius) * dt;
      for (const wheel of wheels) wheel.rotation.x = spin;
      // Front wheels also point where you are steering.
      for (const wheel of frontWheels) wheel.rotation.y = steer * 0.5;
      // Body roll leans out of the turn, exaggerated while sliding.
      const targetRoll = THREE.MathUtils.clamp(-steer * 0.06 - slip * 0.012, -0.16, 0.16);
      roll += (targetRoll - roll) * Math.min(1, dt * 8);
      lowerMesh.rotation.z = roll;
      cabinMesh.rotation.z = roll;
      glassMesh.rotation.z = roll;
      stripeMesh.rotation.z = roll;
    },
    setBraking(braking: boolean) {
      brakeMat.color.setHex(braking ? 0xff3b26 : 0x581410, THREE.SRGBColorSpace);
    },
    dispose() {
      for (const disposable of disposables) disposable.dispose();
      root.removeFromParent();
      root.clear();
    },
  };
}

// ---------------------------------------------------------------------------
// headlights
// ---------------------------------------------------------------------------

/**
 * Lights, built once per car and hung off the car group rather than off either body — so
 * they survive the primitive-to-Ford-Ka swap the same way the contact shadow does.
 *
 * There are no real lights here: eight cars with two spotlights each is sixteen shadow-less
 * spots in a scene already carrying a sun and a hemisphere, and three.js would recompile
 * every material in the city to take them. What is here is the part you actually see at
 * night — a warm pool thrown on the tarmac ahead, lamps that read from the front, and a red
 * glow at the back so a car you are catching is a car and not a hole in the street.
 *
 * Everything is additive, so lighting a car up can only ever add photons: with the
 * intensity at zero the group is `visible = false` and a daytime frame renders exactly what
 * it rendered before any of this existed.
 */
interface CarLights {
  group: THREE.Group;
  set(intensity: number): void;
  dispose(): void;
}

/** Shared across every car on the grid, and never disposed — see `fordKaPrototype`. */
let beamTexture: THREE.Texture | null = null;
let glowTexture: THREE.Texture | null = null;

function sharedBeamTexture(): THREE.Texture {
  if (beamTexture) return beamTexture;
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  // Brightest at the bottom of the image, which is the end nearest the car once the plane
  // is laid flat (v = 0 is the near edge).
  const gradient = ctx.createRadialGradient(size / 2, size - 10, 4, size / 2, size - 10, size);
  gradient.addColorStop(0, 'rgba(255, 248, 226, 1)');
  gradient.addColorStop(0.22, 'rgba(255, 240, 200, 0.82)');
  gradient.addColorStop(0.55, 'rgba(255, 232, 178, 0.42)');
  gradient.addColorStop(1, 'rgba(255, 214, 140, 0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);

  // Cut it to a wedge: narrow at the bumper, spreading out down the road.
  ctx.globalCompositeOperation = 'destination-in';
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.moveTo(size * 0.38, size);
  ctx.lineTo(size * 0.62, size);
  ctx.lineTo(size * 0.98, 0);
  ctx.lineTo(size * 0.02, 0);
  ctx.closePath();
  ctx.fill();

  beamTexture = new THREE.CanvasTexture(canvas);
  beamTexture.colorSpace = THREE.SRGBColorSpace;
  return beamTexture;
}

function sharedGlowTexture(): THREE.Texture {
  if (glowTexture) return glowTexture;
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
  gradient.addColorStop(0.28, 'rgba(255, 240, 200, 0.75)');
  gradient.addColorStop(1, 'rgba(255, 220, 150, 0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  glowTexture = new THREE.CanvasTexture(canvas);
  glowTexture.colorSpace = THREE.SRGBColorSpace;
  return glowTexture;
}

function createCarLights(): CarLights {
  const group = new THREE.Group();
  group.name = 'car-lights';
  group.visible = false;

  const disposables: { dispose(): void }[] = [];
  const materials: { material: THREE.Material; peak: number }[] = [];

  const add = (mesh: THREE.Mesh, material: THREE.MeshBasicMaterial, peak: number) => {
    material.transparent = true;
    material.depthWrite = false;
    material.blending = THREE.AdditiveBlending;
    material.opacity = 0;
    // The pools are flat planes over ground that is not flat. The height above the road
    // covers ordinary camber and kerbs; this covers the rest of a crest, where the far end
    // of a nineteen-metre plane can still meet the hill it is lying over.
    material.polygonOffset = true;
    material.polygonOffsetFactor = -4;
    material.polygonOffsetUnits = -8;
    mesh.renderOrder = 4;
    group.add(mesh);
    materials.push({ material, peak });
    disposables.push(material);
  };

  // The pool on the road. Laid flat, wide enough to light both kerbs at a junction, and
  // long enough to show the next corner before you are in it.
  //
  // `POOL_Y` is the whole reason headlights were invisible on their first outing. A car's
  // `y` is the *terrain* height, exactly — the road ribbon is drawn 0.14 m above that, and
  // the guide line 0.27 m above that again. A pool six centimetres over the car's origin is
  // therefore eight centimetres *under* the tarmac, and the depth test threw it away on
  // every surface anyone ever drives on: the lights only ever showed on bare ground.
  const POOL_Y = 0.34;
  const poolGeo = new THREE.PlaneGeometry(8.5, 19);
  poolGeo.rotateX(-Math.PI / 2);
  poolGeo.translate(0, 0, -10.5);
  const poolMat = new THREE.MeshBasicMaterial({ map: sharedBeamTexture() });
  const pool = new THREE.Mesh(poolGeo, poolMat);
  pool.position.y = POOL_Y;
  add(pool, poolMat, 1);
  disposables.push(poolGeo);

  // A second, short, hot pool right off the bumper. One long gradient alone reads as a haze;
  // the bright patch under the nose is what makes it look like light coming *from* the car.
  const nearGeo = new THREE.PlaneGeometry(5.2, 7);
  nearGeo.rotateX(-Math.PI / 2);
  nearGeo.translate(0, 0, -4.4);
  const nearMat = new THREE.MeshBasicMaterial({ map: sharedBeamTexture() });
  const near = new THREE.Mesh(nearGeo, nearMat);
  near.position.y = POOL_Y;
  add(near, nearMat, 0.45);
  disposables.push(nearGeo);

  // The lamps themselves, facing forward, so an oncoming car has headlights.
  const glowGeo = new THREE.PlaneGeometry(1.6, 1.6);
  for (const side of [-1, 1]) {
    const mat = new THREE.MeshBasicMaterial({ map: sharedGlowTexture() });
    const lamp = new THREE.Mesh(glowGeo, mat);
    lamp.position.set(side * 0.66, 0.72, -2.1);
    lamp.rotation.y = Math.PI;
    add(lamp, mat, 1);
  }
  disposables.push(glowGeo);

  // And red at the back. Dimmer than the brake lights, which have to stay louder than this
  // or braking stops meaning anything.
  const tailGeo = new THREE.PlaneGeometry(0.8, 0.8);
  for (const side of [-1, 1]) {
    const mat = new THREE.MeshBasicMaterial({ map: sharedGlowTexture(), color: 0xff3a20 });
    const tail = new THREE.Mesh(tailGeo, mat);
    tail.position.set(side * 0.64, 0.76, 2.06);
    add(tail, mat, 0.5);
  }
  disposables.push(tailGeo);

  return {
    group,
    set(intensity: number) {
      const amount = Math.max(0, Math.min(1, intensity));
      group.visible = amount > 0.02;
      if (!group.visible) return;
      for (const { material, peak } of materials) material.opacity = amount * peak;
    },
    dispose() {
      for (const disposable of disposables) disposable.dispose();
      group.clear();
    },
  };
}

// ---------------------------------------------------------------------------

export function createCarMesh(color: number): CarMesh {
  const group = new THREE.Group();
  group.name = 'car';

  // A cheap contact shadow — a dark disc that sits just above the road. It belongs to the
  // car rather than to either body, so it survives the swap.
  const shadowGeo = new THREE.CircleGeometry(SHADOW_RADIUS, 20);
  shadowGeo.rotateX(-Math.PI / 2);
  const shadowMat = new THREE.MeshBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0.26,
    depthWrite: false,
  });
  const shadow = new THREE.Mesh(shadowGeo, shadowMat);
  shadow.position.y = SHADOW_HEIGHT;
  shadow.renderOrder = 1;
  group.add(shadow);

  const lights = createCarLights();
  group.add(lights.group);

  let rig = buildPrimitiveRig();
  group.add(rig.root);
  rig.setColor(color);

  let currentColor = color;
  let braking = false;
  let disposed = false;

  void fordKaPrototype().then((prototype) => {
    if (!prototype || disposed) return;
    const next = instantiate(prototype);
    rig.dispose();
    rig = next;
    rig.setColor(currentColor);
    rig.setBraking(braking);
    group.add(rig.root);
  });

  return {
    group,
    setColor(next: number) {
      currentColor = next;
      rig.setColor(next);
    },
    animate(speed: number, steer: number, slip: number, dt: number) {
      rig.animate(speed, steer, slip, dt);
    },
    setBraking(next: boolean) {
      braking = next;
      rig.setBraking(next);
    },
    setLights(intensity: number) {
      lights.set(intensity);
    },
    dispose() {
      disposed = true;
      rig.dispose();
      lights.dispose();
      shadowGeo.dispose();
      shadowMat.dispose();
      group.clear();
    },
  };
}
